import { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler } from 'chart.js';
import API from '../api';
import CDT from './CDT';
import TradingInversiones from './TradingInversiones';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler);

const TODAY = new Date().toISOString().slice(0, 10);

const C = {
  dark: '#08293f',
  muted: '#5a7a8a',
  line: '#e8eef2',
  accent: '#00b8e3',
  up: '#059669',
  down: '#dc2626',
  proj: '#0369a1',
  warn: '#92400e',
};

const COLORES = {
  cdt: '#3b82f6',
  trading: '#00b8e3',
  btc: '#f59e0b',
  acciones: '#8b5cf6',
  divisas: '#0ea5e9',
};

const TIPOS = [
  { key: 'TRADING', label: 'Trading' },
  { key: 'CRIPTO', label: 'Cripto' },
  { key: 'LARGO_PLAZO', label: 'Largo plazo' },
  { key: 'GENERAL', label: 'General' },
];

const usd = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(v) || 0);
const cop = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(v) || 0);
const pct = v => `${(Number(v) || 0).toFixed(2)}%`;
const btcQty = v => (Number(v) || 0).toFixed(8);

// cdt-kpi-grid vive dentro de un <style> en CDT.js, asi que no se puede usar
// desde las otras pestañas. Grilla propia para los KPI.
const kpiGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 };

const emptyAccount = { nombre: '', moneda: 'USD', tipo: 'TRADING', monto_inicial: '', fecha_monto: TODAY };
const emptyBtc = { fecha: TODAY, tipo: 'COMPRA', cantidad: '', precio_usd: '', nota: '' };
const emptyDivisa = { moneda: 'USD', fecha: TODAY, cantidad: '', tasa_compra_cop: '', notas: '' };
const emptyAccion = { broker: 'Trii', ticker: '', nombre: '', fecha: TODAY, cantidad: '', precio_compra_cop: '', comision_cop: '', precio_actual_cop: '' };

const ANIO_ACTUAL = new Date().getFullYear();

export default function Inversiones() {
  const [tab, setTab] = useState('resumen');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Por defecto el año en curso.
  const [anio, setAnio] = useState(ANIO_ACTUAL);

  const load = useCallback(async () => {
    try {
      const r = await API.get(`/inversiones?anio=${anio}`);
      setData(r.data);
      setErr('');
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, [anio]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async fn => {
    setBusy(true);
    try {
      await fn();
      await load();
      setErr('');
      return true;
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [load]);

  if (!data) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 13 }}>
        {err ? `Error: ${err}` : 'Cargando inversiones...'}
      </div>
    );
  }

  const tabs = [
    ['resumen', 'Resumen'],
    ['trading', 'Trading'],
    ['cdt', 'CDT´s'],
    ['btc', 'BTC / Binance'],
    ['acciones', 'Acciones'],
    ['divisas', 'Divisas'],
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: C.dark, margin: 0 }}>Inversiones</h1>
          <p style={{ fontSize: 11, color: C.muted, letterSpacing: '0.06em', marginTop: 4 }}>Trading, CDT, Bitcoin, acciones y divisas en un solo lugar</p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            className="input"
            value={anio}
            onChange={e => setAnio(Number(e.target.value))}
            style={{ width: 92, padding: '6px 8px', fontSize: 12, marginRight: 4 }}
            title="Año de análisis"
          >
            {Array.from({ length: 6 }, (_, i) => ANIO_ACTUAL - i).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {tabs.map(([key, label]) => (
            <button key={key} className={tab === key ? 'btn-primary' : 'btn-secondary'} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
      </div>

      <Frase frases={data.frases} />

      {err && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', color: C.down, borderRadius: 6, padding: '9px 12px', fontSize: 12, marginBottom: 14 }}>
          {err}
        </div>
      )}

      <Dashboard data={data} />

      {tab === 'resumen' && (
        <ResumenTab
          data={data}
          onGo={setTab}
          busy={busy}
          onGuardar={cfg => run(() => API.put('/inversiones/config', cfg))}
          run={run}
          onActualizar={async () => {
            const r = await API.post('/inversiones/tasas/actualizar');
            await load();
            return r.data;
          }}
        />
      )}
      {tab === 'trading' && <TradingTab data={data} run={run} busy={busy} onRecargar={load} />}
      {tab === 'cdt' && (
        <div style={{ marginTop: 18 }}>
          <Evolucion evolucion={data.rendimiento?.cdt?.evolucion} anio={data.rendimiento?.anio} />
          <MovimientosCapital movimientos={data.movimientos} run={run} busy={busy} />
          <CDT embedded anio={anio} />
        </div>
      )}
      {tab === 'btc' && <BtcTab data={data} run={run} busy={busy} />}
      {tab === 'acciones' && <AccionesTab data={data} run={run} busy={busy} />}
      {tab === 'divisas' && <DivisasTab data={data} run={run} busy={busy} />}
    </div>
  );
}

/* ---------------------------------------------------------------- Dashboard */

function Dashboard({ data }) {
  const [consolidar, setConsolidar] = useState(() => {
    try { return localStorage.getItem('inv_consolidar') === '1'; } catch { return false; }
  });

  const toggle = value => {
    setConsolidar(value);
    try { localStorage.setItem('inv_consolidar', value ? '1' : '0'); } catch {}
  };

  const t = data.totales || {};
  const trm = Number(data.trm) || 0;

  const tradingUsd = Number(t.trading_actual_usd || 0);
  const btcUsd = Number(t.btc_valor_usd || 0);
  const btcCosto = Number(t.btc_invertido_usd || 0);
  const cdtCop = Number(t.cdt_activo_cop || 0);
  const accionesCop = Number(t.acciones_valor_cop || 0);
  const accionesCosto = Number(t.acciones_costo_cop || 0);
  const divisasCop = Number(t.divisas_valor_cop || 0);
  const divisasCosto = Number(t.divisas_costo_cop || 0);
  const divisasGanancia = divisasCop - divisasCosto;
  const divisasPct = divisasCosto ? (divisasGanancia / divisasCosto) * 100 : 0;
  const porMoneda = data.divisas_por_moneda || [];

  const usdInicial = Number(t.trading_inicial_usd || 0) + btcCosto;
  const usdActual = tradingUsd + btcUsd;
  const copActual = cdtCop + accionesCop + divisasCop;
  const copInvertido = cdtCop + accionesCosto + divisasCosto;

  const totalCop = copActual + usdActual * trm;
  const share = valueCop => (totalCop > 0 ? (valueCop / totalCop) * 100 : 0);

  const canConsolidate = trm > 0;

  const filas = [
    { label: 'Largo plazo (CDT)', valorCop: cdtCop, color: COLORES.cdt },
    { label: 'Trading', valorCop: tradingUsd * trm, color: COLORES.trading },
    { label: 'Cripto (BTC)', valorCop: btcUsd * trm, color: COLORES.btc },
    { label: 'Acciones', valorCop: accionesCop, color: COLORES.acciones },
    { label: 'Divisas', valorCop: divisasCop, color: COLORES.divisas },
  ].filter(f => f.valorCop > 0);

  return (
    <section className="card" style={{ padding: 18, marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Cartera</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: C.muted, cursor: 'pointer' }}>
            <input type="checkbox" checked={consolidar} onChange={e => toggle(e.target.checked)} style={{ width: 15, height: 15, accentColor: C.accent, cursor: 'pointer' }} />
            Consolidar en pesos
          </label>
          {consolidar && trm > 0 && (
            <span style={{ fontSize: 11, color: C.muted }}>
              TRM {new Intl.NumberFormat('es-CO').format(trm)}
              {data.trm_eur ? ` · EUR ${new Intl.NumberFormat('es-CO').format(Math.round(data.trm_eur))}` : ''}
            </span>
          )}
        </div>
      </div>

      {consolidar && !canConsolidate && (
        <p style={{ fontSize: 12, color: C.down, margin: '0 0 12px' }}>
          Ingresa la TRM en la pestaña Resumen para consolidar. Mientras tanto se muestran las monedas por separado.
        </p>
      )}

      {consolidar && canConsolidate ? (
        <>
          <div style={{ ...kpiGrid, marginBottom: 14 }}>
            <Kpi label="Total cartera" value={cop(totalCop)} accent />
            {filas.map(f => <Kpi key={f.label} label={f.label} value={cop(f.valorCop)} sub={pct(share(f.valorCop))} />)}
          </div>
          <Distribucion rows={filas.map(f => ({ label: f.label, value: share(f.valorCop), color: f.color }))} />
          <p style={{ fontSize: 10, color: C.muted, marginTop: 10, marginBottom: 0 }}>
            Convertido a TRM {new Intl.NumberFormat('es-CO').format(trm)} COP/USD.
            Invertido: {usd(usdInicial)} + {cop(cdtCop + accionesCosto)}.
          </p>
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          <CurrencyBlock
            titulo="En dólares (USD)"
            total={usdActual}
            fmt={usd}
            filas={[
              { label: 'Trading (saldo actual)', valor: tradingUsd },
              { label: 'BTC (valor actual)', valor: btcUsd },
            ]}
            pie={`Invertido ${usd(usdInicial)} · Resultado ${usd(usdActual - usdInicial)}`}
            pieColor={usdActual - usdInicial >= 0 ? C.up : C.down}
          />
          <CurrencyBlock
            titulo="En pesos (COP)"
            total={copActual}
            fmt={cop}
            filas={[
              { label: `CDT activos (${t.cdt_activos || 0})`, valor: cdtCop },
              { label: 'Acciones (valor actual)', valor: accionesCop },
              { label: 'Divisas (valor actual)', valor: divisasCop },
            ]}
            pie={`Invertido ${cop(copInvertido)} · Resultado ${cop(copActual - copInvertido)}`}
            pieColor={copActual - copInvertido >= 0 ? C.up : C.down}
          />
        </div>
      )}

      {porMoneda.length > 0 && (
        <div style={{ marginTop: 16, borderTop: `1px solid ${C.line}`, paddingTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Divisas · ganancia por tasa de cambio
            </span>
            <span style={{
              fontSize: 13, fontWeight: 800, padding: '3px 10px', borderRadius: 5,
              color: divisasGanancia >= 0 ? C.up : C.down,
              background: divisasGanancia >= 0 ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
            }}>
              {divisasGanancia >= 0 ? '+' : ''}{pct(divisasPct)} · {cop(divisasGanancia)}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10 }}>
            {porMoneda.map(m => (
              <div key={m.moneda} style={{ border: `1px solid ${C.line}`, borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <b style={{ fontSize: 13, color: C.dark }}>
                    {m.moneda} {new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(m.cantidad)}
                  </b>
                  <span style={{ fontSize: 12, fontWeight: 800, color: m.ganancia_cop >= 0 ? C.up : C.down }}>
                    {m.ganancia_cop >= 0 ? '+' : ''}{pct(m.porcentaje)}
                  </span>
                </div>
                <Row label="Tasa promedio de compra" value={cop(m.tasa_promedio_compra)} />
                <Row label="Tasa actual" value={m.tasa_actual ? cop(m.tasa_actual) : <span style={{ color: C.warn }}>sin cargar</span>} />
                <Row label="Invertido" value={cop(m.costo_cop)} />
                <Row label="Vale hoy" value={cop(m.valor_cop)} />
                <Row label="Ganancia" value={<span style={{ color: m.ganancia_cop >= 0 ? C.up : C.down }}>{cop(m.ganancia_cop)}</span>} />
              </div>
            ))}
          </div>
          {porMoneda.some(m => !m.tasa_actual) && (
            <p style={{ fontSize: 11, color: C.warn, margin: '9px 0 0' }}>
              Falta cargar la tasa actual de alguna moneda. Marca "Consolidar en pesos" y guárdala para ver la ganancia real.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function CurrencyBlock({ titulo, total, fmt, filas, pie, pieColor }) {
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.dark, marginBottom: 10 }}>{fmt(total)}</div>
      {filas.map(f => (
        <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, padding: '3px 0' }}>
          <span>{f.label}</span>
          <b style={{ color: C.dark }}>{fmt(f.valor)}</b>
        </div>
      ))}
      <div style={{ fontSize: 10, color: pieColor, marginTop: 8, borderTop: `1px solid ${C.line}`, paddingTop: 7 }}>{pie}</div>
    </div>
  );
}

function Distribucion({ rows }) {
  return (
    <div>
      <div style={{ display: 'flex', height: 9, borderRadius: 5, overflow: 'hidden', background: C.line, marginBottom: 9 }}>
        {rows.map(r => r.value > 0 && (
          <div key={r.label} style={{ width: `${r.value}%`, background: r.color }} title={`${r.label}: ${pct(r.value)}`} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {rows.map(r => (
          <span key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted }}>
            <i style={{ width: 8, height: 8, borderRadius: 2, background: r.color, display: 'inline-block' }} />
            {r.label} <b style={{ color: C.dark }}>{pct(r.value)}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent, color }) {
  return (
    <div className="card" style={{ padding: 14, borderTop: accent ? `2px solid ${C.accent}` : undefined }}>
      <small style={{ fontSize: 10, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</small>
      <strong style={{ display: 'block', fontSize: 19, color: color || C.dark, marginTop: 4 }}>{value}</strong>
      {sub && <span style={{ fontSize: 11, color: C.muted }}>{sub}</span>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, padding: '4px 0' }}>
      <span>{label}</span>
      <b style={{ color: C.dark }}>{value}</b>
    </div>
  );
}

function Aviso({ children }) {
  return (
    <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.4)', borderRadius: 6, padding: '10px 13px', fontSize: 12, color: C.warn, marginBottom: 14 }}>
      {children}
    </div>
  );
}

/* --------------------------------------------------------------- Tasas */

function TasasPanel({ data, onGuardar, onActualizar, busy }) {
  const [trm, setTrm] = useState(String(data.trm || ''));
  const [eur, setEur] = useState(String(data.trm_eur || ''));
  const [par, setPar] = useState(String(data.eur_por_usd || ''));
  const [btc, setBtc] = useState(String(data.btc_precio_actual || ''));
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setTrm(String(data.trm || ''));
    setEur(String(data.trm_eur || ''));
    setPar(String(data.eur_por_usd || ''));
    setBtc(String(data.btc_precio_actual || ''));
  }, [data.trm, data.trm_eur, data.eur_por_usd, data.btc_precio_actual]);

  const guardar = () => {
    setMsg('');
    onGuardar({
      trm: Number(trm) || 0,
      trm_eur: Number(eur) || 0,
      eur_por_usd: Number(par) || 0,
      ...(Number(btc) ? { btc_precio: Number(btc) } : {}),
    });
  };

  const actualizar = async () => {
    setMsg('');
    const r = await onActualizar();
    if (r?.errores?.length) setMsg(r.errores.join(' · '));
    else if (r) setMsg('Tasas actualizadas desde internet.');
  };

  // Chequeo cruzado: el euro en pesos deberia ser la TRM dividida por la paridad.
  const implicito = Number(trm) && Number(par) ? Number(trm) / Number(par) : 0;
  const desviacion = implicito && Number(eur) ? Math.abs((Number(eur) - implicito) / implicito) * 100 : 0;

  const actualizado = data.tasas_actualizado
    ? new Date(data.tasas_actualizado).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <section className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Tasas de cambio
          </h2>
          <p style={{ fontSize: 11, color: C.muted, margin: '3px 0 0' }}>
            Se aplican a todo el módulo: cartera, divisas, BTC y portafolio.
            {actualizado ? ` Última actualización automática: ${actualizado}.` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 7 }}>
          <button className="btn-secondary" disabled={busy} onClick={actualizar}>↻ Actualizar de internet</button>
          <button className="btn-primary" disabled={busy} onClick={guardar}>Guardar</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: 12 }}>
        <Field label="TRM hoy (COP por dólar)">
          <input className="input" type="number" step="0.01" value={trm} onChange={e => setTrm(e.target.value)} placeholder="3202.79" />
          <span style={{ display: 'block', fontSize: 10, color: C.muted, marginTop: 3 }}>Oficial de la Superfinanciera</span>
        </Field>
        <Field label="Euro hoy (COP por euro)">
          <input className="input" type="number" step="0.01" value={eur} onChange={e => setEur(e.target.value)} placeholder="3682" />
          <span style={{ display: 'block', fontSize: 10, color: C.muted, marginTop: 3 }}>
            {implicito ? `Implícito por paridad: ${cop(implicito)}` : 'Valora tus euros'}
          </span>
        </Field>
        <Field label="Euro / dólar (euros por dólar)">
          <input className="input" type="number" step="0.00001" value={par} onChange={e => setPar(e.target.value)} placeholder="0.86299" />
          <span style={{ display: 'block', fontSize: 10, color: C.muted, marginTop: 3 }}>
            {Number(par) ? `1 EUR = ${(1 / Number(par)).toFixed(4)} USD` : 'Convierte euros a dólares'}
          </span>
        </Field>
        <Field label="Precio del BTC (USD)">
          <input className="input" type="number" step="0.01" value={btc} onChange={e => setBtc(e.target.value)} placeholder="77816.22" />
          <span style={{ display: 'block', fontSize: 10, color: C.muted, marginTop: 3 }}>Para valorar tu bitcoin</span>
        </Field>
      </div>

      {desviacion > 2 && (
        <p style={{ fontSize: 11, color: C.warn, margin: '10px 0 0' }}>
          El euro que pusiste ({cop(Number(eur))}) se aleja {desviacion.toFixed(1)}% del que sale de cruzar la TRM con la paridad ({cop(implicito)}).
          Puede ser normal si vienen de fuentes distintas, pero revísalo.
        </p>
      )}
      {msg && (
        <p style={{ fontSize: 11, color: msg.startsWith('Tasas') ? C.up : C.down, margin: '10px 0 0' }}>{msg}</p>
      )}
    </section>
  );
}

/* ------------------------------------------------------------- Rendimiento */

const MESES_CORTO = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function Frase({ frases }) {
  // Se elige una al azar en cada carga del modulo.
  const frase = useMemo(() => {
    if (!frases || !frases.length) return null;
    return frases[Math.floor(Math.random() * frases.length)];
  }, [frases]);
  if (!frase) return null;
  return (
    <div style={{
      background: 'linear-gradient(90deg, rgba(0,184,227,0.10), rgba(0,184,227,0.02))',
      borderLeft: `3px solid ${C.accent}`, borderRadius: 6,
      padding: '13px 18px', marginBottom: 16,
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: C.accent, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 }}>
        Mentalidad
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: C.dark, fontStyle: 'italic' }}>{frase}</div>
    </div>
  );
}

function AlertasCdt({ alertas, onGo }) {
  if (!alertas || !alertas.length) return null;
  const vencidos = alertas.filter(a => a.vencido);
  const proximos = alertas.filter(a => !a.vencido);
  return (
    <section className="card" style={{ padding: 16, marginBottom: 14, borderLeft: `3px solid ${vencidos.length ? C.down : '#f59e0b'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {vencidos.length ? 'CDT vencidos sin liquidar' : 'CDT por vencer'}
        </h3>
        <button className="btn-secondary" onClick={() => onGo('cdt')} style={{ padding: '4px 9px', fontSize: 11 }}>Ir a CDT</button>
      </div>
      {vencidos.length > 0 && (
        <p style={{ fontSize: 12, color: C.down, margin: '0 0 10px' }}>
          {vencidos.length} CDT ya vencieron pero siguen marcados como activos. Dejaron de rentar: márcalos como vencidos
          y registra el valor entregado para que el rendimiento del portafolio sea real.
        </p>
      )}
      {[...vencidos, ...proximos].map(a => (
        <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${C.line}`, fontSize: 12 }}>
          <span style={{ color: C.dark, fontWeight: 600 }}>
            {a.entidad} <span style={{ color: C.muted, fontWeight: 400 }}>{cop(a.monto)} al {a.tasa}%</span>
          </span>
          <span style={{ textAlign: 'right' }}>
            <b style={{ color: a.vencido ? C.down : '#b45309' }}>
              {a.vencido ? `venció hace ${Math.abs(a.dias_restantes)} días` : `faltan ${a.dias_restantes} días`}
            </b>
            <span style={{ display: 'block', fontSize: 10, color: C.muted }}>
              {String(a.fecha_vencimiento).slice(0, 10)} · rinde {cop(a.rendimiento_total)}
            </span>
          </span>
        </div>
      ))}
    </section>
  );
}

function RendimientoPanel({ rendimiento, trm, onGo }) {
  const r = rendimiento;
  const hoyMes = new Date().getMonth();
  // Hasta que mes hay datos: en años pasados es diciembre, en el actual el mes de hoy.
  const ultimoMes = r && r.anio < new Date().getFullYear() ? 11 : hoyMes;
  const [mesIdx, setMesIdx] = useState(ultimoMes);
  useEffect(() => { setMesIdx(ultimoMes); }, [ultimoMes]);
  if (!r) return null;

  // El trading y el BTC vienen en dolares: se pasan a pesos para comparar.
  const filas = [
    {
      key: 'cdt', label: 'CDT', color: COLORES.cdt,
      mes: r.cdt.mensual[mesIdx] || 0, pctMes: r.cdt.capital ? ((r.cdt.mensual[mesIdx] || 0) / r.cdt.capital) * 100 : 0,
      anio: r.cdt.anio, pctAnio: r.cdt.pct_anio,
      base: r.cdt.capital, exacto: true,
      nota: 'Causación diaria por tasa y plazo',
    },
    {
      key: 'trading', label: 'Trading', color: COLORES.trading,
      mes: (r.trading.mensual_usd[mesIdx] || 0) * trm,
      pctMes: r.trading.inicial_usd ? ((r.trading.mensual_usd[mesIdx] || 0) / r.trading.inicial_usd) * 100 : 0,
      anio: r.trading.anio_usd * trm, pctAnio: r.trading.pct_anio,
      base: r.trading.inicial_usd * trm, exacto: true,
      nota: 'Resultado real del diario de operaciones',
    },
    {
      key: 'btc', label: 'Bitcoin', color: COLORES.btc,
      mes: r.btc.mensual * trm, pctMes: r.btc.pctMensual,
      anio: r.btc.ganancia * trm, pctAnio: r.btc.pct,
      base: r.btc.costo * trm, exacto: false,
      nota: `Acumulado repartido en ${r.btc.meses} meses`,
    },
    {
      key: 'acciones', label: 'Acciones', color: COLORES.acciones,
      mes: r.acciones.mensual, pctMes: r.acciones.pctMensual,
      anio: r.acciones.ganancia, pctAnio: r.acciones.pct,
      base: r.acciones.costo, exacto: false,
      nota: `Acumulado repartido en ${r.acciones.meses} meses`,
    },
    {
      key: 'divisas', label: 'Divisas', color: COLORES.divisas,
      mes: r.divisas.mensual, pctMes: r.divisas.pctMensual,
      anio: r.divisas.ganancia, pctAnio: r.divisas.pct,
      base: r.divisas.costo, exacto: false,
      nota: `Acumulado repartido en ${r.divisas.meses} meses`,
    },
  ].filter(f => f.base > 0);

  const totMes = filas.reduce((s, f) => s + f.mes, 0);
  const totAnio = filas.reduce((s, f) => s + f.anio, 0);
  const totBase = filas.reduce((s, f) => s + f.base, 0);

  return (
    <section className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: '0 0 4px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Rendimiento
          </h2>
          <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>
            {MESES_CORTO[mesIdx]} de {r.anio} y acumulado del año, sobre el capital invertido en cada activo.
          </p>
        </div>
        <select
          className="input"
          value={mesIdx}
          onChange={e => setMesIdx(Number(e.target.value))}
          style={{ width: 130, padding: '6px 8px', fontSize: 12 }}
        >
          {MESES_CORTO.map((m, i) => (
            <option key={m} value={i} disabled={i > ultimoMes}>{m} {r.anio}</option>
          ))}
        </select>
      </div>

      <div style={{ ...kpiGrid, marginBottom: 16 }}>
        <Kpi label={`Rendimiento ${MESES_CORTO[mesIdx]} ${r.anio}`} value={cop(totMes)} sub={totBase ? pct((totMes / totBase) * 100) : ''} color={totMes >= 0 ? C.up : C.down} accent />
        <Kpi label={`Rendimiento ${r.anio}`} value={cop(totAnio)} sub={totBase ? pct((totAnio / totBase) * 100) : ''} color={totAnio >= 0 ? C.up : C.down} />
        <Kpi label="Capital que renta" value={cop(totBase)} />
        <Kpi label="Proyección 12 meses" value={cop(totMes * 12)} sub={totBase ? `${pct((totMes * 12 / totBase) * 100)} anualizado` : ''} />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Activo</th><th>Capital</th>
              <th>{MESES_CORTO[mesIdx]}</th><th>% mes</th>
              <th>Año {r.anio}</th><th>% año</th>
              <th>Cálculo</th><th />
            </tr>
          </thead>
          <tbody>
            {filas.map(f => (
              <tr key={f.key}>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                    <i style={{ width: 9, height: 9, borderRadius: 3, background: f.color }} />
                    <b style={{ color: C.dark }}>{f.label}</b>
                  </span>
                </td>
                <td>{cop(f.base)}</td>
                <td style={{ color: f.mes >= 0 ? C.up : C.down, fontWeight: 700 }}>{cop(f.mes)}</td>
                <td style={{ color: f.mes >= 0 ? C.up : C.down }}>{pct(f.pctMes)}</td>
                <td style={{ color: f.anio >= 0 ? C.up : C.down, fontWeight: 700 }}>{cop(f.anio)}</td>
                <td style={{ color: f.anio >= 0 ? C.up : C.down }}>{pct(f.pctAnio)}</td>
                <td>
                  <span style={{
                    fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4,
                    color: f.exacto ? C.up : C.warn,
                    background: f.exacto ? 'rgba(5,150,105,0.1)' : 'rgba(251,191,36,0.15)',
                  }} title={f.nota}>{f.exacto ? 'EXACTO' : 'ESTIMADO'}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => onGo(f.key)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>Abrir</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `1px solid var(--app-border)` }}>
              <td><b style={{ color: C.dark }}>Total</b></td>
              <td><b>{cop(totBase)}</b></td>
              <td style={{ color: totMes >= 0 ? C.up : C.down, fontWeight: 800 }}>{cop(totMes)}</td>
              <td style={{ color: totMes >= 0 ? C.up : C.down, fontWeight: 800 }}>{totBase ? pct((totMes / totBase) * 100) : '—'}</td>
              <td style={{ color: totAnio >= 0 ? C.up : C.down, fontWeight: 800 }}>{cop(totAnio)}</td>
              <td style={{ color: totAnio >= 0 ? C.up : C.down, fontWeight: 800 }}>{totBase ? pct((totAnio / totBase) * 100) : '—'}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>

      <p style={{ fontSize: 10, color: C.muted, margin: '10px 0 0', lineHeight: 1.5 }}>
        <b>Exacto</b>: el CDT causa interés día a día según su tasa y plazo, y el trading sale del diario de operaciones.{' '}
        <b>Estimado</b>: BTC, acciones y divisas no tienen historial de precios, así que el mes es el acumulado
        repartido entre los meses que llevas con el activo.
      </p>
    </section>
  );
}



function Evolucion({ evolucion, anio }) {
  const e = evolucion;
  if (!e) return null;
  const crecio = e.hoy - e.inicio_anio;
  const positivo = crecio >= 0;

  // Barra proporcional: cuanto del crecimiento fue rendir y cuanto mover plata.
  const neto = e.capital_neto_movido != null ? e.capital_neto_movido : e.flujo_capital;
  const total = Math.abs(e.rendimiento) + Math.abs(neto) || 1;
  const pRend = (Math.abs(e.rendimiento) / total) * 100;

  const paso = (etiqueta, valor, color, nota) => (
    <div style={{ flex: 1, minWidth: 130 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{etiqueta}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color }}>{cop(valor)}</div>
      {nota && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{nota}</div>}
    </div>
  );

  const flecha = signo => (
    <div style={{ fontSize: 20, color: C.line, fontWeight: 300, alignSelf: 'center', padding: '0 2px' }}>{signo}</div>
  );

  return (
    <section className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Cómo inició {anio} y cómo va
          </h2>
          <p style={{ fontSize: 11, color: C.muted, margin: '3px 0 0' }}>
            Separando lo que rindió de la plata que moviste.
          </p>
        </div>
        <span style={{
          fontSize: 13, fontWeight: 800, padding: '4px 11px', borderRadius: 5,
          color: e.rendimiento >= 0 ? C.up : C.down,
          background: e.rendimiento >= 0 ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
        }}>
          Rindió {cop(e.rendimiento)}
          {e.rentabilidad_periodo != null ? ` · ${pct(e.rentabilidad_periodo)}` : ''}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch', marginBottom: 16 }}>
        {paso('Capital que ya era tuyo', e.capital_propio_inicial != null ? e.capital_propio_inicial : e.inicio_anio,
          C.dark, e.traslados > 0 ? `${cop(e.inicio_anio)} en CDT + ${cop(e.traslados)} por fuera` : 'al arrancar el año')}
        {flecha('−')}
        {paso('Retiros', e.retiros, e.retiros > 0 ? C.down : C.muted, 'plata que salió')}
        {flecha('+')}
        {paso('Riqueza nueva', e.aportes_nuevos || 0, (e.aportes_nuevos || 0) > 0 ? C.proj : C.muted, 'capital que no existía antes')}
        {flecha('+')}
        {paso('Rendimiento', e.rendimiento, C.up, 'lo único que generó sola')}
        {flecha('=')}
        {paso('Hoy', e.hoy, C.accent)}
      </div>

      {e.traslados > 0 && (e.aportes_nuevos || 0) === 0 && (
        <div style={{ background: 'rgba(3,105,161,0.06)', border: '1px solid rgba(3,105,161,0.22)', borderRadius: 6, padding: '10px 13px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: C.proj, fontWeight: 700, marginBottom: 3 }}>
            En {anio} no entró capital nuevo
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>
            Los {cop(e.traslados)} que se ven como aportes ya eran tuyos: estaban por fuera de la cartera y entraron
            a rendir. No son riqueza generada este año, solo cambiaron de lugar.
          </div>
        </div>
      )}

      {e.retiros > 0 && (
        <div style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 6, padding: '10px 13px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: C.down, fontWeight: 700, marginBottom: 3 }}>
            El capital bajó {cop(e.retiros)} por retiros
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>
            Sin ese retiro la cartera estaría en {cop(e.hoy + e.retiros)}. Cuando devuelvas ese capital, regístralo
            como aporte y el indicador lo va reflejando.
          </div>
        </div>
      )}

      {/* De donde sale el saldo de hoy: casi nada es crecimiento propio */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
          De dónde sale el saldo de hoy
        </div>
        {(() => {
          const propio = (e.capital_propio_inicial != null ? e.capital_propio_inicial : e.inicio_anio) - e.retiros;
          const nuevo = e.aportes_nuevos || 0;
          const gen = e.rendimiento;
          const tot = propio + nuevo + gen || 1;
          const p = v => Math.max(0, (v / tot) * 100);
          return (
            <>
              <div style={{ display: 'flex', height: 11, borderRadius: 6, overflow: 'hidden', background: C.line, marginBottom: 9 }}>
                <div style={{ width: `${p(propio)}%`, background: '#94a3b8' }} title={`Ya era tuyo: ${cop(propio)}`} />
                <div style={{ width: `${p(nuevo)}%`, background: C.proj }} title={`Riqueza nueva: ${cop(nuevo)}`} />
                <div style={{ width: `${p(gen)}%`, background: C.up }} title={`Generado: ${cop(gen)}`} />
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: C.muted }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i style={{ width: 9, height: 9, borderRadius: 2, background: '#94a3b8', display: 'inline-block' }} />
                  Capital que ya era tuyo <b style={{ color: C.dark }}>{pct(p(propio))}</b>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i style={{ width: 9, height: 9, borderRadius: 2, background: C.proj, display: 'inline-block' }} />
                  Riqueza nueva del año <b style={{ color: C.dark }}>{pct(p(nuevo))}</b>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i style={{ width: 9, height: 9, borderRadius: 2, background: C.up, display: 'inline-block' }} />
                  Lo generó rindiendo <b style={{ color: C.dark }}>{pct(p(gen))}</b>
                </span>
              </div>
            </>
          );
        })()}
      </div>

      {/* Rentabilidad real, ponderada por el tiempo que cada peso trabajo */}
      {e.rentabilidad_periodo != null && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: 10, background: 'rgba(5,150,105,0.05)', border: '1px solid rgba(5,150,105,0.2)', borderRadius: 8, padding: 13, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>Rentabilidad real</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.up }}>{pct(e.rentabilidad_periodo)}</div>
            <div style={{ fontSize: 10, color: C.muted }}>en {e.dias_periodo} días</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>Anualizada</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.up }}>{pct(e.rentabilidad_anualizada)}</div>
            <div style={{ fontSize: 10, color: C.muted }}>si sigue igual</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>Capital promedio</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.dark }}>{cop(e.capital_promedio)}</div>
            <div style={{ fontSize: 10, color: C.muted }}>el que estuvo trabajando</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, borderTop: `1px solid ${C.line}`, paddingTop: 12 }}>
        <Row label="Capital neto movido" value={
          <span style={{ color: neto >= 0 ? C.proj : C.down }}>{neto >= 0 ? '+' : ''}{cop(neto)}</span>
        } />
        <Row label={`Proyectado a dic ${anio}`} value={cop(e.fin_anio)} />
        <Row label="Falta causar" value={<span style={{ color: C.proj }}>{cop(e.fin_anio - e.hoy)}</span>} />
      </div>

      <p style={{ fontSize: 10, color: C.muted, margin: '10px 0 0', lineHeight: 1.5 }}>
        La rentabilidad se mide contra el capital promedio que estuvo trabajando, pesando cada aporte por los días
        que alcanzó a rendir. Comparar el saldo de hoy contra el de enero no sirve: mezclaría la plata que metiste
        con lo que realmente ganaste.
        {Math.abs(e.sin_explicar || 0) > Math.max(1000000, Math.abs(e.hoy - e.inicio_anio) * 0.005) && (
          <> Hay {cop(Math.abs(e.sin_explicar))} de diferencia entre el movimiento registrado y el cambio real de la
          cartera: falta registrar algún aporte o retiro.</>
        )}
      </p>
    </section>
  );
}


function MovimientosCapital({ movimientos, run, busy }) {
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState({ fecha: TODAY, tipo: 'APORTE', naturaleza: 'PROPIO', monto: '', concepto: '' });

  const lista = movimientos || [];
  const aportes = lista.filter(m => m.tipo === 'APORTE').reduce((s, m) => s + Number(m.monto || 0), 0);
  const retiros = lista.filter(m => m.tipo === 'RETIRO').reduce((s, m) => s + Number(m.monto || 0), 0);

  const submit = async e => {
    e.preventDefault();
    const ok = await run(() => API.post('/inversiones/movimientos', { ...form, monto: Number(form.monto) }));
    if (ok) setForm({ fecha: TODAY, tipo: 'APORTE', naturaleza: 'PROPIO', monto: '', concepto: '' });
  };

  return (
    <section className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Movimientos de capital
          </h2>
          <p style={{ fontSize: 11, color: C.muted, margin: '3px 0 0' }}>
            Plata que entra o sale de la cartera. Reinvertir no cuenta: es el mismo capital rotando.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: C.muted }}>
            <b style={{ color: C.proj }}>+{cop(aportes)}</b> · <b style={{ color: C.down }}>−{cop(retiros)}</b>
          </span>
          <button className="btn-secondary" onClick={() => setAbierto(v => !v)} style={{ padding: '5px 11px', fontSize: 11 }}>
            {abierto ? 'Ocultar' : `Ver (${lista.length})`}
          </button>
        </div>
      </div>

      {abierto && (
        <>
          <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))', gap: 10, alignItems: 'end', margin: '16px 0 14px' }}>
            <Field label="Tipo">
              <select className="input" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                <option value="APORTE">Aporte — entra plata</option>
                <option value="RETIRO">Retiro — sale plata</option>
              </select>
            </Field>
            <Field label="Naturaleza">
              <select className="input" value={form.naturaleza} onChange={e => setForm({ ...form, naturaleza: e.target.value })}>
                <option value="PROPIO">Plata que ya era mía</option>
                <option value="NUEVO">Riqueza nueva</option>
              </select>
            </Field>
            <Field label="Fecha">
              <input className="input" type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required />
            </Field>
            <Field label="Monto">
              <input className="input" type="number" step="1" placeholder="2000000" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} required />
            </Field>
            <Field label="Concepto">
              <input className="input" placeholder="Abono del capital del carro" value={form.concepto} onChange={e => setForm({ ...form, concepto: e.target.value })} />
            </Field>
            <button className="btn-primary" disabled={busy}>Registrar</button>
          </form>

          {!lista.length ? (
            <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Sin movimientos registrados.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead><tr><th>Fecha</th><th>Tipo</th><th>Monto</th><th>Concepto</th><th /></tr></thead>
                <tbody>
                  {lista.map(m => (
                    <tr key={m.id}>
                      <td>{String(m.fecha).slice(0, 10)}</td>
                      <td>
                        <span style={{
                          fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4,
                          color: m.tipo === 'RETIRO' ? C.down : C.proj,
                          background: m.tipo === 'RETIRO' ? 'rgba(220,38,38,0.1)' : 'rgba(3,105,161,0.1)',
                        }}>{m.tipo}</span>
                      </td>
                      <td style={{ fontWeight: 700, color: m.tipo === 'RETIRO' ? C.down : C.dark }}>
                        {m.tipo === 'RETIRO' ? '−' : '+'}{cop(m.monto)}
                      </td>
                      <td style={{ fontSize: 11, color: C.muted }}>{m.concepto || ''}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          onClick={() => window.confirm('¿Eliminar este movimiento?') && run(() => API.delete(`/inversiones/movimientos/${m.id}`))}
                          style={{ background: 'none', border: 'none', color: C.down, cursor: 'pointer', fontSize: 15 }}
                          title="Eliminar"
                        >×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Comparativo({ comparativo }) {
  const c = comparativo;
  if (!c) return null;
  const { actual, anterior, variacion, variacion_pct, mes_corte } = c;
  const positivo = variacion >= 0;

  // Acumulado mes a mes de cada año para verlos lado a lado.
  const acumular = arr => arr.reduce((out, v, i) => {
    out.push((out[i - 1] || 0) + (Number(v) || 0));
    return out;
  }, []);
  const serieAct = acumular(actual.mensual_cdt.map((v, i) => v + (actual.mensual_trading[i] || 0)));
  const serieAnt = acumular(anterior.mensual_cdt.map((v, i) => v + (anterior.mensual_trading[i] || 0)));
  const maximo = Math.max(...serieAct, ...serieAnt, 1);

  const filas = [
    { label: 'CDT y bolsillos', act: actual.cdt, ant: anterior.cdt },
    { label: 'Trading', act: actual.trading, ant: anterior.trading },
  ];

  return (
    <section className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {actual.anio} vs {anterior.anio}
          </h2>
          <p style={{ fontSize: 11, color: C.muted, margin: '3px 0 0' }}>
            Rendimiento acumulado del año. {mes_corte < 11 ? `${actual.anio} va hasta ${MESES_CORTO[mes_corte]}.` : 'Año completo.'}
          </p>
        </div>
        <span style={{
          fontSize: 13, fontWeight: 800, padding: '4px 11px', borderRadius: 5,
          color: positivo ? C.up : C.down,
          background: positivo ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
        }}>
          {positivo ? '+' : ''}{cop(variacion)}
          {variacion_pct != null && Math.abs(variacion_pct) < 1000 ? ` · ${pct(variacion_pct)}` : ''}
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl">
          <thead>
            <tr><th>Concepto</th><th>{actual.anio}</th><th>{anterior.anio}</th><th>Diferencia</th></tr>
          </thead>
          <tbody>
            {filas.map(f => {
              const dif = f.act - f.ant;
              return (
                <tr key={f.label}>
                  <td><b style={{ color: C.dark }}>{f.label}</b></td>
                  <td><b>{cop(f.act)}</b></td>
                  <td style={{ color: C.muted }}>{cop(f.ant)}</td>
                  <td style={{ color: dif >= 0 ? C.up : C.down, fontWeight: 700 }}>{dif >= 0 ? '+' : ''}{cop(dif)}</td>
                </tr>
              );
            })}
            <tr>
              <td style={{ color: C.muted }}>Capital aportado en el año</td>
              <td>{cop(actual.aportes)}</td>
              <td style={{ color: C.muted }}>{cop(anterior.aportes)}</td>
              <td style={{ color: C.muted }}>{cop(actual.aportes - anterior.aportes)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `1px solid var(--app-border)` }}>
              <td><b style={{ color: C.dark }}>Rendimiento total</b></td>
              <td style={{ fontWeight: 800, color: C.dark }}>{cop(actual.total)}</td>
              <td style={{ fontWeight: 700, color: C.muted }}>{cop(anterior.total)}</td>
              <td style={{ color: positivo ? C.up : C.down, fontWeight: 800 }}>{positivo ? '+' : ''}{cop(variacion)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Acumulado mes a mes, los dos años uno sobre otro */}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 14, marginBottom: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted }}>
            <i style={{ width: 9, height: 9, borderRadius: 2, background: C.accent, display: 'inline-block' }} />{actual.anio}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted }}>
            <i style={{ width: 9, height: 9, borderRadius: 2, background: '#cbd9e6', display: 'inline-block' }} />{anterior.anio}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4, height: 90, alignItems: 'end' }}>
          {MESES_CORTO.map((m, i) => (
            <div key={m} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, height: '100%', justifyContent: 'flex-end' }}>
              <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: '100%', width: '100%', justifyContent: 'center' }}>
                <div
                  style={{ width: '42%', background: C.accent, borderRadius: '2px 2px 0 0', height: `${(serieAct[i] / maximo) * 100}%`, minHeight: serieAct[i] > 0 ? 2 : 0 }}
                  title={`${m} ${actual.anio}: ${cop(serieAct[i])}`}
                />
                <div
                  style={{ width: '42%', background: '#cbd9e6', borderRadius: '2px 2px 0 0', height: `${(serieAnt[i] / maximo) * 100}%`, minHeight: serieAnt[i] > 0 ? 2 : 0 }}
                  title={`${m} ${anterior.anio}: ${cop(serieAnt[i])}`}
                />
              </div>
              <span style={{ fontSize: 9, color: C.muted }}>{m}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GraficaCrecimiento({ serie }) {
  if (!serie || serie.length < 2) {
    return (
      <section className="card" style={{ padding: 18, marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: '0 0 8px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Crecimiento en el tiempo
        </h2>
        <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Se necesitan al menos dos meses con datos.</p>
      </section>
    );
  }

  const labels = serie.map(p => {
    const [y, m] = p.mes.split('-');
    return `${MESES_CORTO[Number(m) - 1]} ${String(y).slice(2)}`;
  });

  const series = [
    { key: 'cdt', label: 'CDT', color: COLORES.cdt },
    { key: 'btc', label: 'Bitcoin', color: COLORES.btc },
    { key: 'divisas', label: 'Divisas', color: COLORES.divisas },
    { key: 'trading', label: 'Trading', color: COLORES.trading },
    { key: 'acciones', label: 'Acciones', color: COLORES.acciones },
  ].filter(s => serie.some(p => p[s.key] > 0));

  const data = {
    labels,
    datasets: series.map(s => ({
      label: s.label,
      data: serie.map(p => Math.round(p[s.key])),
      borderColor: s.color,
      backgroundColor: `${s.color}22`,
      borderWidth: 2,
      pointRadius: 2,
      pointHoverRadius: 5,
      tension: 0.25,
      fill: false,
    })),
  };

  const opciones = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 }, usePointStyle: true } },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.dataset.label}: ${cop(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          font: { size: 10 },
          callback: v => {
            const n = Math.abs(v);
            return n >= 1e6 ? `$${(v / 1e6).toFixed(0)}M` : n >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`;
          },
        },
        grid: { color: '#eef2f6' },
      },
      x: { ticks: { font: { size: 10 } }, grid: { display: false } },
    },
  };

  const primero = serie[0], ultimo = serie[serie.length - 1];
  const crecimiento = primero.total ? ((ultimo.total - primero.total) / primero.total) * 100 : 0;

  return (
    <section className="card" style={{ padding: 18, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Crecimiento en el tiempo
          </h2>
          <p style={{ fontSize: 11, color: C.muted, margin: '3px 0 0' }}>
            Valor de cada inversión mes a mes, en pesos.
          </p>
        </div>
        <span style={{
          fontSize: 12, fontWeight: 800, padding: '4px 10px', borderRadius: 5,
          color: crecimiento >= 0 ? C.up : C.down,
          background: crecimiento >= 0 ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
        }}>
          {crecimiento >= 0 ? '+' : ''}{pct(crecimiento)} en {serie.length} meses
        </span>
      </div>
      <div style={{ height: 300, marginTop: 12 }}>
        <Line data={data} options={opciones} />
      </div>
      <p style={{ fontSize: 10, color: C.muted, margin: '10px 0 0' }}>
        El CDT crece con su interés causado y el trading con los resultados del diario. BTC, acciones y divisas se
        muestran al costo de las compras hasta el mes actual, donde ya se valoran a precio de hoy.
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------- Portafolio */

// Riesgo por clase de activo, de 1 (capital garantizado) a 5 (puede perderse todo).
const RIESGOS = {
  1: { label: 'Muy bajo', color: '#059669' },
  2: { label: 'Bajo', color: '#65a30d' },
  3: { label: 'Medio', color: '#d97706' },
  4: { label: 'Alto', color: '#ea580c' },
  5: { label: 'Muy alto', color: '#dc2626' },
};

function ResumenTab({ data, onGo, onGuardar, onActualizar, busy, run }) {
  const t = data.totales || {};
  const trm = Number(data.trm) || 0;
  const huerfanas = data.huerfanas || [];
  const porMoneda = data.divisas_por_moneda || [];

  const aCop = usdVal => usdVal * trm;

  const activos = [
    {
      key: 'cdt', label: 'CDT', tipo: 'Renta fija', riesgo: 1, color: COLORES.cdt,
      valor: Number(t.cdt_activo_cop || 0),
      invertido: Number(t.cdt_capital_cop != null ? t.cdt_capital_cop : t.cdt_activo_cop || 0),
      detalle: `${t.cdt_activos || 0} certificado${t.cdt_activos === 1 ? '' : 's'} activo${t.cdt_activos === 1 ? '' : 's'}`,
      moneda: 'COP',
    },
    {
      key: 'divisas', label: 'Divisas', tipo: 'Cambiario', riesgo: 3, color: COLORES.divisas,
      valor: Number(t.divisas_valor_cop || 0),
      invertido: Number(t.divisas_costo_cop || 0),
      detalle: porMoneda.map(m => `${m.moneda} ${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(m.cantidad)}`).join(' · ') || 'sin compras',
      moneda: 'COP',
    },
    {
      key: 'acciones', label: 'Acciones', tipo: 'Renta variable', riesgo: 4, color: COLORES.acciones,
      valor: Number(t.acciones_valor_cop || 0),
      invertido: Number(t.acciones_costo_cop || 0),
      detalle: `${(data.acciones || []).length} posición${(data.acciones || []).length === 1 ? '' : 'es'}`,
      moneda: 'COP',
    },
    {
      key: 'btc', label: 'Bitcoin', tipo: 'Criptomoneda', riesgo: 5, color: COLORES.btc,
      valor: aCop(Number(t.btc_valor_usd || 0)),
      invertido: aCop(Number(t.btc_invertido_usd || 0)),
      detalle: `${btcQty(data.btc_resumen?.cantidad)} BTC`,
      moneda: 'USD',
    },
    {
      key: 'trading', label: 'Trading', tipo: 'Especulativo', riesgo: 5, color: COLORES.trading,
      valor: aCop(Number(t.trading_actual_usd || 0)),
      invertido: aCop(Number(t.trading_inicial_usd || 0)),
      detalle: `${(data.cuentas || []).length} cuenta${(data.cuentas || []).length === 1 ? '' : 's'}`,
      moneda: 'USD',
    },
  ].filter(a => a.valor > 0);

  const total = activos.reduce((s, a) => s + a.valor, 0);
  const totalInvertido = activos.reduce((s, a) => s + a.invertido, 0);
  const gananciaTotal = total - totalInvertido;

  const conPct = activos
    .map(a => ({ ...a, pct: total ? (a.valor / total) * 100 : 0, ganancia: a.valor - a.invertido }))
    .sort((x, y) => y.valor - x.valor);

  const mayor = conPct[0];
  // Riesgo del portafolio: promedio ponderado por el peso de cada activo.
  const riesgoPonderado = total ? conPct.reduce((s, a) => s + a.riesgo * a.valor, 0) / total : 0;
  const riesgoNivel = RIESGOS[Math.max(1, Math.min(5, Math.round(riesgoPonderado)))];
  // Cuánto pesa lo que puede perder valor frente a lo que no.
  const pctRiesgoso = total ? (conPct.filter(a => a.riesgo >= 4).reduce((s, a) => s + a.valor, 0) / total) * 100 : 0;

  if (!trm) {
    return (
      <div style={{ marginTop: 18 }}>
        <TasasPanel data={data} onGuardar={onGuardar} onActualizar={onActualizar} busy={busy} />
        <Aviso>Ingresa la TRM arriba, o tráela de internet, para ver el portafolio consolidado.</Aviso>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 18 }}>
      <TasasPanel data={data} onGuardar={onGuardar} onActualizar={onActualizar} busy={busy} />

      <AlertasCdt alertas={data.rendimiento?.alertas_cdt} onGo={onGo} />
      <Evolucion evolucion={data.rendimiento?.cdt?.evolucion} anio={data.rendimiento?.anio} />
      <MovimientosCapital movimientos={data.movimientos} run={run} busy={busy} />
      <RendimientoPanel rendimiento={data.rendimiento} trm={trm} onGo={onGo} />
      <Comparativo comparativo={data.rendimiento?.comparativo} />
      <GraficaCrecimiento serie={data.rendimiento?.serie} />

      {huerfanas.length > 0 && (
        <Aviso>
          Tienes operaciones en cuentas que aún no has creado:{' '}
          <b>{huerfanas.map(h => `${h.cuenta} (${h.operaciones})`).join(', ')}</b>. Créalas en Trading con su monto inicial.
        </Aviso>
      )}

      {/* Encabezado del portafolio */}
      <section className="card" style={{ padding: 18, marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: '0 0 14px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Mi portafolio
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 18 }}>
          <Kpi label="Valor total" value={cop(total)} accent />
          <Kpi label="Invertido" value={cop(totalInvertido)} />
          <Kpi
            label="Ganancia acumulada"
            value={cop(gananciaTotal)}
            sub={totalInvertido ? `${gananciaTotal >= 0 ? '+' : ''}${pct((gananciaTotal / totalInvertido) * 100)} desde la compra` : ''}
            color={gananciaTotal >= 0 ? C.up : C.down}
          />
          <Kpi label="Clases de activo" value={conPct.length} sub={conPct.map(a => a.label).join(' · ')} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
          <Donut items={conPct} total={total} />
          <div style={{ minWidth: 220 }}>
            {conPct.map(a => (
              <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0' }}>
                <i style={{ width: 10, height: 10, borderRadius: 3, background: a.color, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 12, color: C.dark, fontWeight: 600 }}>{a.label}</span>
                <span style={{ fontSize: 12, color: C.muted, minWidth: 92, textAlign: 'right' }}>{cop(a.valor)}</span>
                <b style={{ fontSize: 12, color: C.dark, minWidth: 52, textAlign: 'right' }}>{pct(a.pct)}</b>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Concentracion y riesgo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 14 }}>
        <section className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            Mayor concentración
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 26, fontWeight: 800, color: mayor.color }}>{pct(mayor.pct)}</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.dark }}>{mayor.label}</span>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{cop(mayor.valor)} · {mayor.tipo}</div>
          <div style={{ height: 7, borderRadius: 4, background: C.line, overflow: 'hidden' }}>
            <div style={{ width: `${mayor.pct}%`, height: '100%', background: mayor.color }} />
          </div>
          {mayor.pct >= 60 && (
            <p style={{ fontSize: 11, color: C.warn, margin: '9px 0 0' }}>
              Más de la mitad del portafolio está en un solo activo. Diversificar reduce el impacto si ese activo cae.
            </p>
          )}
        </section>

        <section className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            Nivel de riesgo del portafolio
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: riesgoNivel.color }}>{riesgoNivel.label}</span>
            <span style={{ fontSize: 12, color: C.muted }}>{riesgoPonderado.toFixed(2)} de 5</span>
          </div>
          <div style={{ display: 'flex', gap: 3, marginBottom: 10 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <div key={n} style={{
                flex: 1, height: 7, borderRadius: 3,
                background: n <= Math.round(riesgoPonderado) ? RIESGOS[n].color : C.line,
              }} />
            ))}
          </div>
          <Row label="En activos de alto riesgo" value={pct(pctRiesgoso)} />
          <Row label="En renta fija (CDT)" value={pct(conPct.find(a => a.key === 'cdt')?.pct || 0)} />
          <p style={{ fontSize: 10, color: C.muted, margin: '8px 0 0', lineHeight: 1.5 }}>
            Ponderado por el peso de cada activo. El grueso está en CDT, que es capital garantizado, por eso el riesgo general se mantiene bajo.
          </p>
        </section>
      </div>

      {/* Detalle por clase de activo */}
      <section className="card" style={{ padding: 0, overflowX: 'auto', marginBottom: 14 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Activo</th><th>Tipo</th><th>Contiene</th><th>Invertido</th><th>Valor hoy</th>
              <th>Ganancia acum.</th><th>%</th><th>Peso</th><th>Riesgo</th><th />
            </tr>
          </thead>
          <tbody>
            {conPct.map(a => (
              <tr key={a.key}>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                    <i style={{ width: 9, height: 9, borderRadius: 3, background: a.color }} />
                    <b style={{ color: C.dark }}>{a.label}</b>
                  </span>
                </td>
                <td style={{ fontSize: 11, color: C.muted }}>{a.tipo}</td>
                <td style={{ fontSize: 11, color: C.muted }}>{a.detalle}</td>
                <td>{cop(a.invertido)}</td>
                <td><b>{cop(a.valor)}</b></td>
                <td style={{ color: a.ganancia >= 0 ? C.up : C.down, fontWeight: 700 }}>{cop(a.ganancia)}</td>
                <td style={{ color: a.ganancia >= 0 ? C.up : C.down, fontWeight: 700 }}>
                  {a.invertido ? pct((a.ganancia / a.invertido) * 100) : '—'}
                </td>
                <td><b>{pct(a.pct)}</b></td>
                <td>
                  <span style={{
                    fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 4,
                    color: RIESGOS[a.riesgo].color, background: `${RIESGOS[a.riesgo].color}1a`,
                  }}>{RIESGOS[a.riesgo].label}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => onGo(a.key)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>Abrir</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `1px solid var(--app-border)` }}>
              <td colSpan={3}><b style={{ color: C.dark }}>Total</b></td>
              <td><b>{cop(totalInvertido)}</b></td>
              <td><b>{cop(total)}</b></td>
              <td style={{ color: gananciaTotal >= 0 ? C.up : C.down, fontWeight: 800 }}>{cop(gananciaTotal)}</td>
              <td style={{ color: gananciaTotal >= 0 ? C.up : C.down, fontWeight: 800 }}>
                {totalInvertido ? pct((gananciaTotal / totalInvertido) * 100) : '—'}
              </td>
              <td><b>100.00%</b></td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </section>

      <p style={{ fontSize: 10, color: C.muted, margin: 0 }}>
        La ganancia es <b>acumulada desde que compraste cada activo</b>, no de un mes ni de un año: es
        cuánto vale hoy tu portafolio frente a lo que pusiste. Convertido a pesos con TRM{' '}
        {new Intl.NumberFormat('es-CO').format(trm)}
        {data.trm_eur ? ` y euro a ${new Intl.NumberFormat('es-CO').format(Math.round(data.trm_eur))}` : ''}.
        Los CDT y bolsillos se valoran con su interes causado a hoy, capitalizado sobre la tasa efectiva anual.
      </p>
    </div>
  );
}

function Donut({ items, total }) {
  const R = 58, GROSOR = 22;
  const circ = 2 * Math.PI * R;
  let acumulado = 0;
  return (
    <svg width={150} height={150} viewBox="0 0 150 150" role="img" aria-label="Composición del portafolio">
      <g transform="rotate(-90 75 75)">
        <circle cx="75" cy="75" r={R} fill="none" stroke={C.line} strokeWidth={GROSOR} />
        {items.map(a => {
          const largo = total ? (a.valor / total) * circ : 0;
          const offset = -acumulado;
          acumulado += largo;
          return (
            <circle
              key={a.key}
              cx="75" cy="75" r={R} fill="none"
              stroke={a.color} strokeWidth={GROSOR}
              strokeDasharray={`${largo} ${circ - largo}`}
              strokeDashoffset={offset}
            >
              <title>{`${a.label}: ${pct((a.valor / total) * 100)}`}</title>
            </circle>
          );
        })}
      </g>
      <text x="75" y="71" textAnchor="middle" style={{ fontSize: 11, fill: C.muted, fontWeight: 600 }}>Activos</text>
      <text x="75" y="90" textAnchor="middle" style={{ fontSize: 22, fill: C.dark, fontWeight: 800 }}>{items.length}</text>
    </svg>
  );
}

/* ------------------------------------------------------------------ Trading */

function TradingTab({ data, run, busy, onRecargar }) {
  const [account, setAccount] = useState(emptyAccount);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const cuentas = data.cuentas || [];
  const huerfanas = data.huerfanas || [];

  const submit = async e => {
    e.preventDefault();
    const payload = { ...account, monto_inicial: Number(account.monto_inicial) || 0 };
    const ok = await run(() => (editId ? API.put(`/trading/cuentas/${editId}`, payload) : API.post('/trading/cuentas', payload)));
    if (ok) { setAccount(emptyAccount); setEditId(null); setShowForm(false); }
  };

  const editar = c => {
    setAccount({
      nombre: c.nombre,
      moneda: c.moneda,
      tipo: c.tipo || 'TRADING',
      monto_inicial: c.monto_inicial,
      fecha_monto: c.fecha_monto ? String(c.fecha_monto).slice(0, 10) : TODAY,
    });
    setEditId(c.id);
    setShowForm(true);
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Cuentas</h2>
        <button className="btn-primary" onClick={() => { setAccount(emptyAccount); setEditId(null); setShowForm(v => !v); }}>
          {showForm ? 'Cancelar' : '+ Nueva cuenta'}
        </button>
      </div>

      {huerfanas.length > 0 && (
        <Aviso>
          Operaciones sin cuenta creada:{' '}
          {huerfanas.map(h => (
            <button
              key={h.cuenta}
              onClick={() => { setAccount({ ...emptyAccount, nombre: h.cuenta }); setEditId(null); setShowForm(true); }}
              style={{ background: 'none', border: 'none', color: C.warn, textDecoration: 'underline', cursor: 'pointer', fontWeight: 800, fontSize: 12, fontFamily: 'inherit', padding: 0, marginRight: 8 }}
            >
              {h.cuenta} ({h.operaciones} ops, {usd(h.resultado)}) → crear
            </button>
          ))}
        </Aviso>
      )}

      {showForm && (
        <section className="card" style={{ padding: 16, marginBottom: 16 }}>
          <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, alignItems: 'end' }}>
            <Field label="Nombre de la cuenta">
              <input className="input" placeholder="Exness 1" value={account.nombre} onChange={e => setAccount({ ...account, nombre: e.target.value })} required />
            </Field>
            <Field label="Moneda de la cuenta">
              <select className="input" value={account.moneda} onChange={e => setAccount({ ...account, moneda: e.target.value })}>
                <option value="USD">Dólares</option>
                <option value="CENTAVOS_USD">Centavos de dólar</option>
              </select>
            </Field>
            <Field label="Clasificación">
              <select className="input" value={account.tipo} onChange={e => setAccount({ ...account, tipo: e.target.value })}>
                {TIPOS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Monto inicial">
              <input className="input" type="number" step="0.01" placeholder="0.00" value={account.monto_inicial} onChange={e => setAccount({ ...account, monto_inicial: e.target.value })} required />
            </Field>
            <Field label="Fecha del monto">
              <input className="input" type="date" value={account.fecha_monto} onChange={e => setAccount({ ...account, fecha_monto: e.target.value })} />
            </Field>
            <button className="btn-primary" disabled={busy}>{editId ? 'Guardar cambios' : 'Crear cuenta'}</button>
          </form>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 10, marginBottom: 0 }}>
            El nombre debe coincidir con el que usas al registrar operaciones. El monto inicial es la base del porcentaje.
          </p>
        </section>
      )}

      {!cuentas.length ? (
        <p style={{ fontSize: 12, color: C.muted }}>Crea tu primera cuenta para ver saldo y porcentaje.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 22 }}>
          {cuentas.map(c => {
            const centavos = c.moneda === 'CENTAVOS_USD';
            return (
              <section key={c.id} className="card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.dark }}>{c.nombre}</div>
                    <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {centavos ? 'Centavos de dólar' : 'Dólares'} · {TIPOS.find(t => t.key === (c.tipo || 'TRADING'))?.label}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: 800,
                    color: Number(c.porcentaje) >= 0 ? C.up : C.down,
                    background: Number(c.porcentaje) >= 0 ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
                    padding: '3px 8px', borderRadius: 5,
                  }}>{pct(c.porcentaje)}</span>
                </div>

                <div style={{ fontSize: 21, fontWeight: 800, color: C.dark }}>{usd(c.saldo_usd)}</div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>saldo actual en dólares</div>

                <Row label="Monto inicial" value={usd(c.inicial_usd)} />
                <Row label="Resultado operaciones" value={<span style={{ color: Number(c.resultado_usd) >= 0 ? C.up : C.down }}>{usd(c.resultado_usd)}</span>} />
                <Row label="Rendimiento semanal" value={<span style={{ color: Number(c.porcentaje_semana) >= 0 ? C.up : C.down }}>{pct(c.porcentaje_semana)} · {usd(c.resultado_semana_usd)}</span>} />
                <Row label="Rendimiento mensual" value={<span style={{ color: Number(c.porcentaje_mes) >= 0 ? C.up : C.down }}>{pct(c.porcentaje_mes)} · {usd(c.resultado_mes_usd)}</span>} />
                <Row label="Operaciones" value={c.operaciones} />
                {centavos && (
                  <p style={{ fontSize: 10, color: C.muted, margin: '6px 0 0' }}>
                    Registras en centavos ({Number(c.monto_inicial).toFixed(2)}), se muestra convertido a dólares.
                  </p>
                )}

                <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                  <button className="btn-secondary" style={{ padding: '4px 9px', fontSize: 11 }} onClick={() => editar(c)}>Editar monto inicial</button>
                  {!c.operaciones && (
                    <button
                      className="btn-secondary"
                      style={{ padding: '4px 9px', fontSize: 11, color: C.down }}
                      onClick={() => window.confirm(`¿Eliminar la cuenta ${c.nombre}?`) && run(() => API.delete(`/trading/cuentas/${c.id}`))}
                    >Eliminar</button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${C.line}`, paddingTop: 18 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: '0 0 12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Operaciones</h2>
        <TradingInversiones embedded onCambio={onRecargar} />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- BTC */

function BtcTab({ data, run, busy }) {
  const [form, setForm] = useState(emptyBtc);
  const [showForm, setShowForm] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [precioInput, setPrecioInput] = useState(String(data.btc_precio_actual || ''));

  const resumen = data.btc_resumen || {};
  const movimientos = data.btc || [];

  const cantidad = Number(resumen.cantidad) || 0;
  const costo = Number(resumen.total_usd) || 0;
  const promedio = Number(resumen.precio_promedio) || 0;
  const precioActual = Number(resumen.precio_actual) || 0;
  const valorActual = Number(resumen.valor_actual) || 0;
  const ganancia = valorActual - costo;

  const sinRegistro = movimientos.filter(m => String(m.nota || '').startsWith('SIN REGISTRO'));

  const proyeccion = useMemo(() => {
    const precio = Number(salePrice);
    if (!precio || !cantidad) return null;
    const valor = precio * cantidad;
    const g = valor - costo;
    return { valor, ganancia: g, porcentaje: costo ? (g / costo) * 100 : 0 };
  }, [salePrice, cantidad, costo]);

  const submit = async e => {
    e.preventDefault();
    const ok = await run(() => API.post('/inversiones/btc', {
      ...form,
      cantidad: Number(form.cantidad),
      precio_usd: Number(form.precio_usd),
    }));
    if (ok) { setForm(emptyBtc); setShowForm(false); }
  };

  const total = (Number(form.cantidad) || 0) * (Number(form.precio_usd) || 0);

  return (
    <div style={{ marginTop: 18 }}>
      {sinRegistro.length > 0 && (
        <Aviso>
          <b>{btcQty(sinRegistro.reduce((s, m) => s + Number(m.cantidad), 0))} BTC</b> están cargados como saldo estimado porque no hay
          historial de esas compras. El precio se dedujo del costo que reporta Binance. Edítalo o reemplázalo cuando tengas los registros reales.
        </Aviso>
      )}

      <div style={{ ...kpiGrid, marginBottom: 16 }}>
        <Kpi label="BTC" value={btcQty(cantidad)} accent />
        <Kpi label="Precio promedio" value={usd(promedio)} sub={`${movimientos.length} movimiento${movimientos.length === 1 ? '' : 's'}`} />
        <Kpi label="Costo" value={usd(costo)} />
        <Kpi label="Valor actual" value={usd(valorActual)} sub={precioActual ? `a ${usd(precioActual)}` : 'sin precio'} />
        <Kpi label="Ganancia" value={usd(ganancia)} sub={costo ? pct((ganancia / costo) * 100) : ''} color={ganancia >= 0 ? C.up : C.down} />
      </div>

      <section className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <Field label="Precio actual del BTC (USD)">
            <input className="input" type="number" step="0.01" value={precioInput} onChange={e => setPrecioInput(e.target.value)} placeholder="77816.22" style={{ width: 150 }} />
          </Field>
          <button className="btn-secondary" disabled={busy || !Number(precioInput)} onClick={() => run(() => API.put('/inversiones/config', { btc_precio: Number(precioInput) }))}>
            Actualizar precio
          </button>
        </div>
      </section>

      <section className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: '0 0 12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Simular venta</h2>
        <Field label="Precio de venta por BTC (USD)">
          <input className="input" type="number" step="0.01" placeholder="95000" value={salePrice} onChange={e => setSalePrice(e.target.value)} style={{ width: 160 }} />
        </Field>
        {!cantidad ? (
          <p style={{ fontSize: 12, color: C.muted, marginTop: 10, marginBottom: 0 }}>Registra al menos una compra para simular.</p>
        ) : proyeccion ? (
          <div style={{ ...kpiGrid, marginTop: 14 }}>
            <Kpi label="Valor de venta" value={usd(proyeccion.valor)} />
            <Kpi label="Ganancia" value={usd(proyeccion.ganancia)} sub={pct(proyeccion.porcentaje)} color={proyeccion.ganancia >= 0 ? C.up : C.down} />
            <Kpi label="Precio promedio de compra" value={usd(promedio)} sub={`Venta a ${usd(Number(salePrice))}`} />
          </div>
        ) : (
          <p style={{ fontSize: 12, color: C.muted, marginTop: 10, marginBottom: 0 }}>Ingresa un precio probable de venta.</p>
        )}
      </section>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Movimientos</h2>
        <button className="btn-primary" onClick={() => setShowForm(v => !v)}>{showForm ? 'Cancelar' : '+ Movimiento'}</button>
      </div>

      {showForm && (
        <section className="card" style={{ padding: 16, marginBottom: 16 }}>
          <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, alignItems: 'end' }}>
            <Field label="Tipo">
              <select className="input" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                <option value="COMPRA">Compra</option>
                <option value="VENTA">Venta</option>
              </select>
            </Field>
            <Field label="Fecha">
              <input className="input" type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required />
            </Field>
            <Field label="Cantidad BTC">
              <input className="input" type="number" step="0.00000001" placeholder="0.00250000" value={form.cantidad} onChange={e => setForm({ ...form, cantidad: e.target.value })} required />
            </Field>
            <Field label="Precio USD por BTC">
              <input className="input" type="number" step="0.01" placeholder="88000" value={form.precio_usd} onChange={e => setForm({ ...form, precio_usd: e.target.value })} required />
            </Field>
            <Field label="Total">
              <div style={{ fontSize: 15, fontWeight: 700, color: C.dark, padding: '7px 0' }}>{usd(total)}</div>
            </Field>
            <Field label="Nota (opcional)">
              <input className="input" placeholder="Referencia" value={form.nota} onChange={e => setForm({ ...form, nota: e.target.value })} />
            </Field>
            <button className="btn-primary" disabled={busy}>Registrar</button>
          </form>
        </section>
      )}

      <section className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="tbl">
          <thead>
            <tr><th>Fecha</th><th>Tipo</th><th>Cantidad</th><th>Precio USD</th><th>Total</th><th>vs promedio</th><th>Nota</th><th /></tr>
          </thead>
          <tbody>
            {!movimientos.length ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: C.muted, padding: 18, fontSize: 12 }}>Sin movimientos registrados.</td></tr>
            ) : movimientos.map(b => {
              const esVenta = b.tipo === 'VENTA';
              const diff = promedio ? ((Number(b.precio_usd) - promedio) / promedio) * 100 : 0;
              return (
                <tr key={b.id}>
                  <td>{String(b.fecha).slice(0, 10)}</td>
                  <td>
                    <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 4, color: esVenta ? C.down : C.up, background: esVenta ? 'rgba(220,38,38,0.1)' : 'rgba(5,150,105,0.1)' }}>
                      {esVenta ? 'VENTA' : 'COMPRA'}
                    </span>
                  </td>
                  <td>{btcQty(b.cantidad)}</td>
                  <td>{usd(b.precio_usd)}</td>
                  <td>{usd(b.total_usd)}</td>
                  <td style={{ color: diff <= 0 ? C.up : C.down }}>{diff > 0 ? '+' : ''}{diff.toFixed(2)}%</td>
                  <td style={{ fontSize: 11, color: String(b.nota || '').startsWith('SIN REGISTRO') ? C.warn : C.muted }}>{b.nota || ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => window.confirm('¿Eliminar este movimiento?') && run(() => API.delete(`/inversiones/btc/${b.id}`))}
                      style={{ background: 'none', border: 'none', color: C.down, cursor: 'pointer', fontSize: 15, padding: '0 6px' }}
                      title="Eliminar"
                    >×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/* ----------------------------------------------------------------- Acciones */

// Todas las compras de una accion juntas: precio promedio y un solo precio de hoy.
function ResumenAccion({ r, run, busy }) {
  const [precio, setPrecio] = useState('');
  const fmtFecha = f => new Date(f).toLocaleString('es-CO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  // Si el ultimo precio vino de internet, se muestra de donde sale (dolares x TRM de hoy)
  const ref = r.referencia;
  const refVigente = ref && (!r.precio_actualizado || new Date(ref.actualizado).getTime() >= new Date(r.precio_actualizado).getTime() - 60000);
  const usd = v => `US$${Number(v).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const actualizado = refVigente
    ? `${ref.moneda === 'USD' ? `${usd(ref.precio)} × TRM ${cop(ref.trm)}` : 'Bolsa'} · ${fmtFecha(ref.actualizado)} (Yahoo Finance)`
    : r.precio_actualizado ? `Actualizado ${fmtFecha(r.precio_actualizado)}` : null;
  const guardar = async e => {
    e.preventDefault();
    const ok = await run(() => API.put('/inversiones/acciones-precio', { ticker: r.ticker, precio_actual_cop: Number(precio) }));
    if (ok) setPrecio('');
  };
  const dato = (label, value, sub, color) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: color || C.dark, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
  return (
    <section className="card" style={{ padding: 16, marginBottom: 12, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <b style={{ fontSize: 16, color: C.dark }}>{r.ticker}</b>
          <span style={{ fontSize: 12, color: C.muted, marginLeft: 8 }}>{r.nombre} · {r.compras} compra{r.compras === 1 ? '' : 's'}</span>
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: r.ganancia_cop >= 0 ? C.up : C.down }}>
          {r.ganancia_cop >= 0 ? '+' : ''}{cop(r.ganancia_cop)} ({r.porcentaje >= 0 ? '+' : ''}{pct(r.porcentaje)})
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
        {dato('Acciones', Number(r.cantidad).toLocaleString('es-CO'))}
        {dato('Precio promedio', cop(r.precio_promedio_cop), `${cop(r.precio_promedio_comision_cop)} con comisión`)}
        {dato('Costo total', cop(r.costo_total_cop), `${cop(r.comisiones_cop)} en comisiones`)}
        {dato('Precio hoy', cop(r.precio_actual_cop), actualizado || 'Precio de la última compra', r.precio_actual_cop >= r.precio_promedio_cop ? C.up : C.down)}
        {dato('Valor hoy', cop(r.valor_actual_cop))}
      </div>
      <form onSubmit={guardar} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="input" type="number" step="0.01" min="0.01" placeholder={`Precio de hoy de ${r.ticker} (COP)`} value={precio}
          onChange={e => setPrecio(e.target.value)} required style={{ maxWidth: 240 }} />
        <button className="btn-primary" disabled={busy || !precio}>Actualizar precio</button>
        <span style={{ fontSize: 11, color: C.muted }}>Se aplica a las {r.compras} compras de {r.ticker}. Úsalo para poner el precio exacto de Trii.</span>
      </form>
    </section>
  );
}

function AccionesTab({ data, run, busy }) {
  const [form, setForm] = useState(emptyAccion);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [precioMsg, setPrecioMsg] = useState('');

  const traerPrecios = async () => {
    setPrecioMsg('');
    await run(async () => {
      const { data: r } = await API.post('/inversiones/acciones-precio/internet');
      const partes = (r.acciones || []).map(a => a.moneda === 'USD'
        ? `${a.ticker}: US$${Number(a.precio).toLocaleString('es-CO', { maximumFractionDigits: 2 })} × TRM ${cop(a.trm)} = ${cop(a.precio_cop)}`
        : `${a.ticker}: ${cop(a.precio_cop)}`);
      setPrecioMsg([...partes, ...(r.errores || []).map(e => `No se pudo: ${e}`)].join(' · '));
    });
  };

  const acciones = data.acciones || [];
  const t = data.totales || {};
  const costo = Number(t.acciones_costo_cop || 0);
  const valor = Number(t.acciones_valor_cop || 0);
  const ganancia = valor - costo;

  const submit = async e => {
    e.preventDefault();
    const payload = {
      ...form,
      cantidad: Number(form.cantidad),
      precio_compra_cop: Number(form.precio_compra_cop),
      comision_cop: Number(form.comision_cop) || 0,
      precio_actual_cop: form.precio_actual_cop === '' ? null : Number(form.precio_actual_cop),
    };
    const ok = await run(() => (editId ? API.put(`/inversiones/acciones/${editId}`, payload) : API.post('/inversiones/acciones', payload)));
    if (ok) { setForm(emptyAccion); setEditId(null); setShowForm(false); }
  };

  const editar = a => {
    setForm({
      broker: a.broker, ticker: a.ticker, nombre: a.nombre,
      fecha: a.fecha ? String(a.fecha).slice(0, 10) : TODAY,
      cantidad: a.cantidad, precio_compra_cop: a.precio_compra_cop,
      comision_cop: a.comision_cop, precio_actual_cop: a.precio_actual_cop,
    });
    setEditId(a.id);
    setShowForm(true);
  };

  const totalCompra = (Number(form.cantidad) || 0) * (Number(form.precio_compra_cop) || 0);

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ ...kpiGrid, marginBottom: 16 }}>
        <Kpi label="Valor actual" value={cop(valor)} accent />
        <Kpi label="Invertido (con comisión)" value={cop(costo)} />
        <Kpi label="Ganancia" value={cop(ganancia)} sub={costo ? pct((ganancia / costo) * 100) : ''} color={ganancia >= 0 ? C.up : C.down} />
        <Kpi label="Posiciones" value={(data.acciones_resumen || []).length} sub={`${acciones.length} compra${acciones.length === 1 ? '' : 's'}`} />
      </div>

      {acciones.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <button className="btn-primary" onClick={traerPrecios} disabled={busy}>{busy ? 'Consultando…' : 'Traer precios de hoy'}</button>
          <span style={{ fontSize: 11, color: C.muted, flex: '1 1 260px' }}>
            Trae el precio en la bolsa de EE. UU. y lo pasa a pesos con la TRM oficial de hoy (también actualiza la TRM).
            Es una referencia: el precio en Trii puede variar un poco. Tu costo de compra no cambia.
          </span>
          {precioMsg && <div style={{ flexBasis: '100%', fontSize: 12, color: C.dark, background: 'rgba(0,184,227,0.08)', borderRadius: 6, padding: '7px 10px' }}>{precioMsg}</div>}
        </div>
      )}

      {(data.acciones_resumen || []).map(r => <ResumenAccion key={r.ticker} r={r} run={run} busy={busy} />)}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Posiciones</h2>
        <button className="btn-primary" onClick={() => { setForm(emptyAccion); setEditId(null); setShowForm(v => !v); }}>
          {showForm ? 'Cancelar' : '+ Nueva acción'}
        </button>
      </div>

      {showForm && (
        <section className="card" style={{ padding: 16, marginBottom: 16 }}>
          <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, alignItems: 'end' }}>
            <Field label="Broker">
              <input className="input" placeholder="Trii" value={form.broker} onChange={e => setForm({ ...form, broker: e.target.value })} required />
            </Field>
            <Field label="Ticker">
              <input className="input" placeholder="NU" value={form.ticker} onChange={e => setForm({ ...form, ticker: e.target.value })} required />
            </Field>
            <Field label="Nombre">
              <input className="input" placeholder="Nu Holdings" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required />
            </Field>
            <Field label="Fecha de compra">
              <input className="input" type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required />
            </Field>
            <Field label="Cantidad">
              <input className="input" type="number" step="0.000001" placeholder="10" value={form.cantidad} onChange={e => setForm({ ...form, cantidad: e.target.value })} required />
            </Field>
            <Field label="Precio compra (COP)">
              <input className="input" type="number" step="0.01" placeholder="46200" value={form.precio_compra_cop} onChange={e => setForm({ ...form, precio_compra_cop: e.target.value })} required />
            </Field>
            <Field label="Comisión (COP)">
              <input className="input" type="number" step="0.01" placeholder="15000" value={form.comision_cop} onChange={e => setForm({ ...form, comision_cop: e.target.value })} />
            </Field>
            <Field label="Precio hoy (COP)">
              <input className="input" type="number" step="0.01" placeholder="48972" value={form.precio_actual_cop} onChange={e => setForm({ ...form, precio_actual_cop: e.target.value })} />
            </Field>
            <Field label="Invertido">
              <div style={{ fontSize: 14, fontWeight: 700, color: C.dark, padding: '7px 0' }}>{cop(totalCompra)}</div>
            </Field>
            <button className="btn-primary" disabled={busy}>{editId ? 'Guardar cambios' : 'Registrar'}</button>
          </form>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 10, marginBottom: 0 }}>
            El porcentaje se calcula contra el costo total, comisión incluida. El precio de hoy es uno solo por acción: si lo escribes aquí, se usa para todas las compras de esa acción.
          </p>
        </section>
      )}

      {!acciones.length ? (
        <p style={{ fontSize: 12, color: C.muted }}>Registra tu primera acción.</p>
      ) : (
        <section className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Broker</th><th>Acción</th><th>Cant.</th><th>Compra</th><th>Invertido</th>
                <th>Comisión</th><th>Costo total</th><th>Hoy</th><th>Valor</th><th>Ganancia</th><th>%</th><th />
              </tr>
            </thead>
            <tbody>
              {acciones.map(a => (
                <tr key={a.id}>
                  <td style={{ fontSize: 11, color: C.muted }}>{a.broker}</td>
                  <td>
                    <b style={{ color: C.dark }}>{a.ticker}</b>
                    <span style={{ display: 'block', fontSize: 10, color: C.muted }}>{a.nombre}</span>
                  </td>
                  <td>{Number(a.cantidad)}</td>
                  <td>{cop(a.precio_compra_cop)}</td>
                  <td>{cop(a.invertido_cop)}</td>
                  <td style={{ color: C.muted }}>{cop(a.comision_cop)}</td>
                  <td><b>{cop(a.costo_total_cop)}</b></td>
                  <td>
                    {cop(a.precio_actual_cop)}
                    <span style={{ display: 'block', fontSize: 10, color: a.porcentaje_precio >= 0 ? C.up : C.down }}>
                      {a.porcentaje_precio >= 0 ? '+' : ''}{pct(a.porcentaje_precio)}
                    </span>
                  </td>
                  <td><b>{cop(a.valor_actual_cop)}</b></td>
                  <td style={{ color: a.ganancia_cop >= 0 ? C.up : C.down, fontWeight: 700 }}>{cop(a.ganancia_cop)}</td>
                  <td style={{ color: a.porcentaje >= 0 ? C.up : C.down, fontWeight: 700 }}>{pct(a.porcentaje)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => editar(a)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, padding: '0 5px', fontFamily: 'inherit' }}>Editar</button>
                    <button
                      onClick={() => window.confirm(`¿Eliminar ${a.ticker}?`) && run(() => API.delete(`/inversiones/acciones/${a.id}`))}
                      style={{ background: 'none', border: 'none', color: C.down, cursor: 'pointer', fontSize: 15, padding: '0 5px' }}
                      title="Eliminar"
                    >×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- Divisas */

function DivisasTab({ data, run, busy }) {
  const [form, setForm] = useState(emptyDivisa);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const divisas = data.divisas || [];
  const porMoneda = data.divisas_por_moneda || [];
  const t = data.totales || {};
  const costo = Number(t.divisas_costo_cop || 0);
  const valor = Number(t.divisas_valor_cop || 0);
  const ganancia = valor - costo;

  const num = v => new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(Number(v) || 0);

  const submit = async e => {
    e.preventDefault();
    const payload = {
      ...form,
      cantidad: Number(form.cantidad),
      tasa_compra_cop: Number(form.tasa_compra_cop),
    };
    const ok = await run(() => (editId ? API.put(`/inversiones/divisas/${editId}`, payload) : API.post('/inversiones/divisas', payload)));
    if (ok) { setForm(emptyDivisa); setEditId(null); setShowForm(false); }
  };

  const editar = d => {
    setForm({
      moneda: d.moneda,
      fecha: d.fecha ? String(d.fecha).slice(0, 10) : TODAY,
      cantidad: d.cantidad,
      tasa_compra_cop: d.tasa_compra_cop,
      notas: d.notas || '',
    });
    setEditId(d.id);
    setShowForm(true);
  };

  const invertido = (Number(form.cantidad) || 0) * (Number(form.tasa_compra_cop) || 0);

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ ...kpiGrid, marginBottom: 16 }}>
        <Kpi label="Valor actual" value={cop(valor)} accent />
        <Kpi label="Invertido" value={cop(costo)} />
        <Kpi label="Ganancia por cambio" value={cop(ganancia)} sub={costo ? pct((ganancia / costo) * 100) : ''} color={ganancia >= 0 ? C.up : C.down} />
        <Kpi label="Compras" value={divisas.length} />
      </div>

      {porMoneda.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12, marginBottom: 18 }}>
          {porMoneda.map(m => (
            <section key={m.moneda} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.dark }}>{m.moneda} {num(m.cantidad)}</div>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {m.compras} compra{m.compras === 1 ? '' : 's'} · equivale a {usd(m.equivalente_usd)}
                  </div>
                </div>
                <span style={{
                  fontSize: 12, fontWeight: 800, padding: '3px 8px', borderRadius: 5,
                  color: m.ganancia_cop >= 0 ? C.up : C.down,
                  background: m.ganancia_cop >= 0 ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
                }}>{m.ganancia_cop >= 0 ? '+' : ''}{pct(m.porcentaje)}</span>
              </div>
              <Row label="Tasa promedio de compra" value={cop(m.tasa_promedio_compra)} />
              <Row label="Tasa actual" value={m.tasa_actual ? cop(m.tasa_actual) : <span style={{ color: C.warn }}>sin cargar</span>} />
              <Row label="Invertido" value={cop(m.costo_cop)} />
              <Row label="Vale hoy" value={cop(m.valor_cop)} />
              <Row label="Ganancia" value={<span style={{ color: m.ganancia_cop >= 0 ? C.up : C.down }}>{cop(m.ganancia_cop)}</span>} />
            </section>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.dark, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Compras</h2>
        <button className="btn-primary" onClick={() => { setForm(emptyDivisa); setEditId(null); setShowForm(v => !v); }}>
          {showForm ? 'Cancelar' : '+ Nueva compra'}
        </button>
      </div>

      {showForm && (
        <section className="card" style={{ padding: 16, marginBottom: 16 }}>
          <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, alignItems: 'end' }}>
            <Field label="Moneda">
              <select className="input" value={form.moneda} onChange={e => setForm({ ...form, moneda: e.target.value })}>
                <option value="USD">Dólares (USD)</option>
                <option value="EUR">Euros (EUR)</option>
              </select>
            </Field>
            <Field label="Fecha de compra">
              <input className="input" type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required />
            </Field>
            <Field label={`Cuánto compraste (${form.moneda})`}>
              <input className="input" type="number" step="0.01" placeholder="200" value={form.cantidad} onChange={e => setForm({ ...form, cantidad: e.target.value })} required />
            </Field>
            <Field label="A cuánto (COP por unidad)">
              <input className="input" type="number" step="0.01" placeholder="4100" value={form.tasa_compra_cop} onChange={e => setForm({ ...form, tasa_compra_cop: e.target.value })} required />
            </Field>
            <Field label="Pagaste">
              <div style={{ fontSize: 14, fontWeight: 700, color: C.dark, padding: '7px 0' }}>{cop(invertido)}</div>
            </Field>
            <Field label="Nota (opcional)">
              <input className="input" placeholder="Casa de cambio" value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} />
            </Field>
            <button className="btn-primary" disabled={busy}>{editId ? 'Guardar cambios' : 'Registrar'}</button>
          </form>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 10, marginBottom: 0 }}>
            La ganancia se calcula comparando la tasa a la que compraste contra la tasa actual que guardas en el dashboard.
          </p>
        </section>
      )}

      {!divisas.length ? (
        <p style={{ fontSize: 12, color: C.muted }}>Registra tu primera compra de divisas.</p>
      ) : (
        <section className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Fecha</th><th>Moneda</th><th>Cantidad</th><th>En USD</th><th>Tasa compra</th><th>Pagaste</th>
                <th>Tasa hoy</th><th>Vale hoy</th><th>Ganancia</th><th>%</th><th>Nota</th><th />
              </tr>
            </thead>
            <tbody>
              {divisas.map(d => (
                <tr key={d.id}>
                  <td>{String(d.fecha).slice(0, 10)}</td>
                  <td><b style={{ color: C.dark }}>{d.moneda}</b></td>
                  <td>{num(d.cantidad)}</td>
                  <td style={{ color: C.muted }}>{usd(d.equivalente_usd)}</td>
                  <td>{cop(d.tasa_compra_cop)}</td>
                  <td>{cop(d.costo_cop)}</td>
                  <td style={{ color: d.sin_tasa ? C.warn : undefined }}>{d.sin_tasa ? 'sin cargar' : cop(d.tasa_actual_cop)}</td>
                  <td><b>{cop(d.valor_cop)}</b></td>
                  <td style={{ color: d.ganancia_cop >= 0 ? C.up : C.down, fontWeight: 700 }}>{cop(d.ganancia_cop)}</td>
                  <td style={{ color: d.porcentaje >= 0 ? C.up : C.down, fontWeight: 700 }}>{pct(d.porcentaje)}</td>
                  <td style={{ fontSize: 11, color: C.muted }}>{d.notas || ''}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => editar(d)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, padding: '0 5px', fontFamily: 'inherit' }}>Editar</button>
                    <button
                      onClick={() => window.confirm('¿Eliminar esta compra?') && run(() => API.delete(`/inversiones/divisas/${d.id}`))}
                      style={{ background: 'none', border: 'none', color: C.down, cursor: 'pointer', fontSize: 15, padding: '0 5px' }}
                      title="Eliminar"
                    >×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- Utils */

function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 10, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}
